// Transition screen (the `transition` step). Twin of the desktop TransitionPage.
//
// Full-screen, researcher-chosen background color with auto contrast text
// (black/white), centered bold text in slots: high-center pinned near the top,
// then a vertically-centered cluster (center-center, the animated line, the
// countdown, an optional input, the main box, and low-center). Ends after a
// timer (with optional ticking countdown), on a click anywhere, or on Enter in
// the input box. An optional pause keeps the screen up before advancing.
//
// Parity notes vs desktop: line sizes scale off the block font_size with the
// same multipliers (high 1.0, center 0.9, animated/countdown/main-box 0.8, low
// 0.8, input prompt 0.7); the animated line cycles 0-3 trailing dots every
// 400ms; the contrast rule matches desktop (luminance > 0.5 -> black).
// Deferred to a later Phase 5 pass (flagged): the optional image and the
// appear/disappear sounds, which both need a session-scoped media fetch the
// online API does not expose yet.

import { useEffect, useRef, useState } from "react";
import type { WebRendererProps } from "./registry";
import type { DocMidBlockSurvey, VideoSurveyResponse } from "../api/types";
import { fetchTransition } from "../session/sessionDriver";
import { SurveyForm, type SurveyResponse } from "./survey/SurveyForm";

const DEFAULT_BG = "#000000"; // matches the desktop transition default (black)

// Black or white, whichever contrasts with the background. Mirrors desktop
// _contrast_color: luminance > 0.5 -> black, else white.
function contrastColor(bg: string): string {
  const hex = bg.replace("#", "");
  const full = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return "#ffffff";
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#000000" : "#ffffff";
}

export default function TransitionRenderer({ step, session, onDone }: WebRendererProps) {
  const settings = (step.settings ?? {}) as Record<string, unknown>;

  const fontSize = typeof settings.font_size === "number" ? settings.font_size : 22;
  const tc = (settings.text_content ?? {}) as Record<string, string>;
  const bg =
    typeof settings.background_color === "string" && settings.background_color
      ? settings.background_color
      : DEFAULT_BG;
  const fg = contrastColor(bg);

  const endCondition = (settings.end_condition as string) ?? "timed";
  const durationSeconds =
    typeof settings.duration_seconds === "number" ? settings.duration_seconds : 5;
  const showCountdown = Boolean(settings.show_countdown) && endCondition === "timed";
  const showAnimated = Boolean(settings.show_animated_text);
  const animatedText =
    typeof settings.animated_text === "string" ? settings.animated_text : "Loading";
  const inputEnabled = endCondition === "input";
  const userInputCfg = (settings.user_input ?? {}) as Record<string, unknown>;
  const inputPrompt =
    typeof userInputCfg.prompt === "string" ? userInputCfg.prompt : "Please enter a value:";
  const pauseAfter =
    typeof settings.pause_after_seconds === "number" ? settings.pause_after_seconds : 0;

  const [dots, setDots] = useState(0);
  const [remaining, setRemaining] = useState(durationSeconds);
  const [inputValue, setInputValue] = useState("");
  const endedRef = useRef(false);
  const startedAt = useRef<number>(performance.now());

  // Mid-block survey state (mirrors WritingRenderer): time triggers, fired-once
  // tracking, the in-flight survey that gates + defers any end, captured
  // responses, and the poll timers.
  const [activeSurvey, setActiveSurvey] = useState<DocMidBlockSurvey | null>(null);
  const triggersRef = useRef<DocMidBlockSurvey[]>([]);
  const firedRef = useRef<Set<number>>(new Set());
  const awaitingSurveyRef = useRef(false);
  const pendingEndRef = useRef<{ endTrigger: "timed_auto" | "manual"; userInput: string } | null>(null);
  const surveyResponsesRef = useRef<VideoSurveyResponse[]>([]);
  const activeSurveyRef = useRef<DocMidBlockSurvey | null>(null);
  const surveyTimersRef = useRef<number[]>([]);

  function finish(endTrigger: "timed_auto" | "manual", userInput: string) {
    // Never end out from under an open mid-block survey; honor it on close.
    if (awaitingSurveyRef.current) {
      pendingEndRef.current = { endTrigger, userInput };
      return;
    }
    if (endedRef.current) return;
    endedRef.current = true;
    surveyTimersRef.current.forEach((t) => window.clearInterval(t));
    const submit = () =>
      onDone({
        kind: "submit",
        result: {
          end_trigger: endTrigger,
          user_input: userInput,
          survey_responses: surveyResponsesRef.current,
        },
      });
    if (pauseAfter > 0) window.setTimeout(submit, pauseAfter * 1000);
    else submit();
  }

  function checkSurveyTriggers(elapsedSeconds: number) {
    if (awaitingSurveyRef.current || endedRef.current) return;
    const list = triggersRef.current;
    for (let i = 0; i < list.length; i++) {
      if (firedRef.current.has(i)) continue;
      if (elapsedSeconds >= list[i].value) {
        firedRef.current.add(i);
        awaitingSurveyRef.current = true;
        activeSurveyRef.current = list[i];
        setActiveSurvey(list[i]);
        return;
      }
      break; // value-sorted: the first unreached trigger ends the scan
    }
  }

  function onSurveySubmit(responses: SurveyResponse[]) {
    const trigger = activeSurveyRef.current;
    if (trigger) {
      surveyResponsesRef.current.push({
        survey_id: trigger.survey_id,
        survey_name: trigger.survey_name,
        responses,
      });
    }
    activeSurveyRef.current = null;
    setActiveSurvey(null);
    awaitingSurveyRef.current = false;
    const pending = pendingEndRef.current;
    if (pending) {
      pendingEndRef.current = null;
      finish(pending.endTrigger, pending.userInput);
    }
  }

  // Animated dots: base text + 0..3 trailing dots, cycling every 400ms.
  useEffect(() => {
    if (!showAnimated) return;
    const id = window.setInterval(() => setDots((d) => (d + 1) % 4), 400);
    return () => clearInterval(id);
  }, [showAnimated]);

  // Timed end + optional countdown.
  useEffect(() => {
    if (endCondition !== "timed") return;
    const endId = window.setTimeout(() => finish("timed_auto", ""), durationSeconds * 1000);
    let cdId: number | undefined;
    if (showCountdown) {
      cdId = window.setInterval(() => setRemaining((r) => (r > 0 ? r - 1 : 0)), 1000);
    }
    return () => {
      clearTimeout(endId);
      if (cdId) clearInterval(cdId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Arm mid-block surveys: fetch resolved triggers (with inline questions) and
  // poll them against the elapsed clock. Non-blocking; a fetch failure never
  // traps the participant (the transition still ends normally).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const content = await fetchTransition(session);
        if (!alive) return;
        triggersRef.current = [...(content.mid_block_surveys ?? [])].sort(
          (a, b) => a.value - b.value,
        );
        if (triggersRef.current.length > 0) {
          const iv = window.setInterval(() => {
            checkSurveyTriggers((performance.now() - startedAt.current) / 1000);
          }, 500);
          surveyTimersRef.current.push(iv);
        }
      } catch {
        /* no surveys; the transition runs normally */
      }
    })();
    return () => {
      alive = false;
      surveyTimersRef.current.forEach((t) => window.clearInterval(t));
      surveyTimersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const onClickScreen = () => {
    if (endCondition === "click") finish("manual", "");
  };
  const submitInput = () => {
    if (inputValue.trim()) finish("manual", inputValue);
  };

  const sizeFor = (mult: number) => ({
    fontSize: `${Math.round(fontSize * mult)}px`,
    fontWeight: 700 as const,
  });
  const countdownText = remaining > 0 ? `Continuing in ${remaining}...` : "Continuing now...";

  return (
    <div className="jol-transition" style={{ background: bg, color: fg }} onClick={onClickScreen}>
      {tc.high_center ? (
        <div className="jol-tr-line" style={sizeFor(1.0)}>
          {tc.high_center}
        </div>
      ) : null}

      <div className="jol-tr-spacer" />

      <div className="jol-tr-cluster">
        {tc.center_center ? (
          <div className="jol-tr-line" style={sizeFor(0.9)}>
            {tc.center_center}
          </div>
        ) : null}
        {showAnimated ? (
          <div className="jol-tr-line" style={sizeFor(0.8)}>
            {animatedText + ".".repeat(dots)}
          </div>
        ) : null}
        {showCountdown ? (
          <div className="jol-tr-line" style={sizeFor(0.8)}>
            {countdownText}
          </div>
        ) : null}
        {inputEnabled ? (
          <>
            <div className="jol-tr-line" style={sizeFor(0.7)}>
              {inputPrompt}
            </div>
            <input
              className="jol-tr-input"
              style={{ fontSize: `${Math.round(fontSize * 0.7)}px` }}
              value={inputValue}
              autoFocus
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitInput();
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </>
        ) : null}
        {tc.main_box ? (
          <div className="jol-tr-mainbox" style={sizeFor(0.8)}>
            {tc.main_box}
          </div>
        ) : null}
        {tc.low_center ? (
          <div className="jol-tr-line" style={sizeFor(0.8)}>
            {tc.low_center}
          </div>
        ) : null}
      </div>

      <div className="jol-tr-spacer" />

      {activeSurvey ? (
        <div className="jol-task-survey-overlay" onClick={(e) => e.stopPropagation()}>
          <SurveyForm
            questions={activeSurvey.questions}
            submitLabel="Submit"
            onSubmit={onSurveySubmit}
          />
        </div>
      ) : null}
    </div>
  );
}
